import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Play,
  Pause,
  Trash2,
  Pencil,
  Check,
  X,
  Volume2,
  VolumeX,
  MoreHorizontal,
  RefreshCw,
  MoveHorizontal,
  Mic,
  Square,
  RotateCcw,
  Save,
  Download,
} from "lucide-react";
import { downloadFile } from "../../utils/downloadFile";
import { audioBufferToWav } from "../../utils/audioBufferToWav";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import WaveSurfer from "wavesurfer.js";
import LoopNudgeControls from "./LoopNudgeControls";

/**
 * Takes list component — shows saved recording takes with tabbed waveforms.
 * Each take gets a muted purple (#9b8ec4) WaveSurfer waveform.
 * Supports playback, rename, delete per take.
 * A-B loop markers with nudge controls and loop-record integration.
 */
const TakesList = ({
  takes = [],
  activeTakeNumber,
  isLoadingTakes = false,
  onSelectTake,
  onDeleteTake,
  onRenameTake,
  getTakeUrl,
  compact = false,
  isMobile = false,
  // WaveSurfer instance callback — called with ws on ready, null on destroy
  onWaveSurferReady = null,
  // Shift props
  onShiftOffsetChange = null,
  // Beat duration for visual shift alignment
  beatDuration = 0,
  // Loop
  loopActive = false,
  onToggleLoop = null,
  // Loop region change callback — called with {start, end} or null
  onLoopRegionChange = null,
  // Loop-record props
  loopRecordState = "idle",
  onStartLoopRecord = null,
  onStopLoopRecord = null,
  onCancelLoopRecord = null,
  onAcceptSplice = null,
  onRetrySplice = null,
  loopRecordSplicedBlob = null,
  loopRecordError = null,
  // Live waveform data callback for loop-record visualization (desktop)
  getAnalyserData = null,
  // Regular recording props (non-loop)
  recorder = null,
  onSaveTake = null,
}) => {
  const [playingTakeNumber, setPlayingTakeNumber] = useState(null);
  const [editingTakeNumber, setEditingTakeNumber] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showShiftView, setShowShiftView] = useState(false);
  const [tempShiftOffset, setTempShiftOffset] = useState(0);
  const regionsPluginRef = useRef(null);

  // Regular recording post-recording state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [labelInput, setLabelInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const previewAudioRef = useRef(null);

  // A-B loop marker state
  const [loopStart, setLoopStart] = useState(null);
  const [loopEnd, setLoopEnd] = useState(null);
  const currentRegionRef = useRef(null);
  const regionEventHandlerRef = useRef(null);

  // Live waveform canvas for loop-record (desktop)
  const liveCanvasRef = useRef(null);
  const liveAnimFrameRef = useRef(null);

  const isLoopRecording =
    loopRecordState === "recording" || loopRecordState === "pre_roll";

  // Live waveform drawing loop (replicates RecordingControls canvas)
  const drawLiveWaveform = useCallback(() => {
    const canvas = liveCanvasRef.current;
    if (!canvas || !getAnalyserData) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const width = canvas.width;
    const height = canvas.height;
    const data = getAnalyserData();

    ctx.clearRect(0, 0, width, height);

    if (!data) {
      liveAnimFrameRef.current = requestAnimationFrame(drawLiveWaveform);
      return;
    }

    ctx.strokeStyle = "#e85d4a";
    ctx.lineWidth = 2;
    ctx.beginPath();

    const sliceWidth = width / data.length;
    let x = 0;

    for (let i = 0; i < data.length; i++) {
      const v = data[i] / 128.0;
      const y = (v * height) / 2;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
      x += sliceWidth;
    }

    ctx.stroke();

    if (loopRecordState === "recording") {
      const pulse = Math.sin(Date.now() / 300) * 0.3 + 0.7;
      ctx.shadowBlur = 8 * pulse;
      ctx.shadowColor = "rgba(232, 93, 74, 0.5)";
    }

    liveAnimFrameRef.current = requestAnimationFrame(drawLiveWaveform);
  }, [getAnalyserData, loopRecordState]);

  // Whether regular (non-loop) recording is active
  const isRegularRecording = !loopActive && recorder?.isRecording;

  useEffect(() => {
    if ((isLoopRecording || isRegularRecording) && getAnalyserData) {
      drawLiveWaveform();
    }
    return () => {
      if (liveAnimFrameRef.current) {
        cancelAnimationFrame(liveAnimFrameRef.current);
        liveAnimFrameRef.current = null;
      }
    };
  }, [isLoopRecording, isRegularRecording, getAnalyserData, drawLiveWaveform]);

  // Create preview URL when recorder produces a blob (regular recording)
  useEffect(() => {
    if (recorder?.audioBlob && !loopActive) {
      const url = URL.createObjectURL(recorder.audioBlob);
      setPreviewUrl(url);
      setLabelInput("");
      return () => URL.revokeObjectURL(url);
    } else if (!recorder?.audioBlob) {
      setPreviewUrl(null);
    }
  }, [recorder?.audioBlob, loopActive]);

  // Handle save for regular recording
  const handleSaveRecording = useCallback(async () => {
    if (!recorder?.audioBlob || !onSaveTake) return;
    setIsSaving(true);
    try {
      await onSaveTake(
        recorder.audioBlob,
        labelInput || undefined,
        recorder.recordingDuration,
      );
      recorder.resetRecording();
      setPreviewUrl(null);
      setLabelInput("");
    } catch (err) {
      console.error("❌ Error saving take:", err);
    } finally {
      setIsSaving(false);
    }
  }, [recorder, onSaveTake, labelInput]);

  // Handle re-record
  const handleReRecord = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    setIsPreviewPlaying(false);
    if (recorder) recorder.resetRecording();
    setPreviewUrl(null);
    setLabelInput("");
  }, [recorder]);

  // Download a take as WAV
  const handleDownloadTake = useCallback(async (take) => {
    try {
      const url = await getTakeUrl(take);
      if (!url) throw new Error("No audio URL");

      const response = await fetch(url);
      const audioBlob = await response.blob();

      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      audioContext.close();

      const wavBlob = audioBufferToWav(audioBuffer);
      const filename = `${take.label || `Take ${take.takeNumber}`}.wav`;
      await downloadFile(wavBlob, filename, "audio/wav");
    } catch (err) {
      console.error("❌ Failed to download take:", err);
    }
  }, [getTakeUrl]);

  // Toggle preview playback
  const togglePreview = useCallback(() => {
    if (!previewAudioRef.current) return;
    if (isPreviewPlaying) {
      previewAudioRef.current.pause();
      setIsPreviewPlaying(false);
    } else {
      previewAudioRef.current.play();
      setIsPreviewPlaying(true);
    }
  }, [isPreviewPlaying]);

  // Handle mic button click — starts regular or loop recording
  const handleMicClick = useCallback(() => {
    if (loopActive) {
      // Loop recording
      if (loopRecordState === "idle" && onStartLoopRecord) {
        onStartLoopRecord();
      } else if (isLoopRecording && onStopLoopRecord) {
        onStopLoopRecord();
      }
    } else if (recorder) {
      // Regular recording
      if (!recorder.isRecording) {
        recorder.startRecording();
      } else {
        recorder.stopRecording();
      }
    }
  }, [
    loopActive,
    loopRecordState,
    isLoopRecording,
    onStartLoopRecord,
    onStopLoopRecord,
    recorder,
  ]);

  // Whether any recording is in progress (loop or regular)
  const isAnyRecording = isLoopRecording || isRegularRecording;

  // Close shift view when loop activates
  useEffect(() => {
    if (loopActive) setShowShiftView(false);
  }, [loopActive]);

  const waveSurferRef = useRef(null);
  const containerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const onWsReadyRef = useRef(onWaveSurferReady);
  const onLoopRegionChangeRef = useRef(onLoopRegionChange);

  // Keep refs in sync
  useEffect(() => {
    onWsReadyRef.current = onWaveSurferReady;
  }, [onWaveSurferReady]);

  useEffect(() => {
    onLoopRegionChangeRef.current = onLoopRegionChange;
  }, [onLoopRegionChange]);

  // Format time as M:SS
  const formatTime = (sec) => {
    if (isNaN(sec)) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Resolve take URL — delegates to getTakeUrl which handles caching and
  // invalidation (e.g. after replaceTakeAudio revokes the old blob URL).
  const resolveTakeUrl = useCallback(
    async (take) => {
      if (!getTakeUrl) return null;
      return await getTakeUrl(take);
    },
    [getTakeUrl],
  );

  // Initialize/reload WaveSurfer when active take changes
  useEffect(() => {
    if (!activeTakeNumber || !containerRef.current) return;

    const activeTake = takes.find((t) => t.takeNumber === activeTakeNumber);
    if (!activeTake) return;

    let isCancelled = false;

    const initWaveSurfer = async () => {
      // Destroy previous instance
      if (waveSurferRef.current) {
        waveSurferRef.current.destroy();
        waveSurferRef.current = null;
      }

      // Clear loop region refs when WaveSurfer is being rebuilt
      currentRegionRef.current = null;
      regionEventHandlerRef.current = null;

      // Reset duration so the loop region effect re-fires when ws is ready
      // (without this, same-duration rebuilds won't recreate the A-B region)
      setDuration(0);

      // Wait for container to mount and CSS to settle
      await new Promise((r) => setTimeout(r, 150));
      if (isCancelled || !containerRef.current) return;

      const url = await resolveTakeUrl(activeTake);
      if (isCancelled || !url) return;

      const regions = RegionsPlugin.create();
      regionsPluginRef.current = regions;

      const ws = WaveSurfer.create({
        container: containerRef.current,
        waveColor: "#7ab892",
        progressColor: "#c5a34e",
        cursorColor: "#c5a34e",
        barWidth: 3,
        barGap: 1,
        barRadius: 1,
        responsive: true,
        height: compact ? 16 : 24,
        normalize: true,
        mediaControls: false,
        interact: true,
        backend: "WebAudio",
        fillParent: true,
        plugins: [regions],
      });

      // Constrain WaveSurfer's host div to prevent it from exceeding container bounds
      const wsHostDiv = containerRef.current.querySelector(
        ":scope > div:last-child",
      );
      if (wsHostDiv) {
        wsHostDiv.style.display = "block";
        wsHostDiv.style.width = "100%";
        wsHostDiv.style.maxWidth = "100%";
        wsHostDiv.style.overflow = "hidden";
        wsHostDiv.style.boxSizing = "border-box";
      }

      ws.on("ready", () => {
        if (isCancelled) return;
        setDuration(ws.getDuration());
      });

      ws.on("timeupdate", (time) => {
        if (isCancelled) return;
        setCurrentTime(time);
      });

      ws.on("play", () => {
        if (!isCancelled) setPlayingTakeNumber(activeTakeNumber);
      });
      ws.on("pause", () => {
        if (!isCancelled) setPlayingTakeNumber(null);
      });
      ws.on("finish", () => {
        if (isCancelled) return;
        // A-B looping is handled by the timeupdate handler
        setPlayingTakeNumber(null);
      });

      try {
        await ws.load(url);
        if (isCancelled) {
          ws.destroy();
          return;
        }
        waveSurferRef.current = ws;

        // Notify parent that WaveSurfer instance is ready
        if (onWsReadyRef.current) onWsReadyRef.current(ws);
        // Double-rAF ensures layout has fully settled before re-measuring
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!isCancelled && containerRef.current) {
              // Force WaveSurfer to re-render at correct container width
              window.dispatchEvent(new Event("resize"));
            }
          });
        });
      } catch (err) {
        console.error("❌ TakesList WaveSurfer error:", err);
        if (!isCancelled) ws.destroy();
      }
    };

    initWaveSurfer();

    return () => {
      isCancelled = true;
      if (waveSurferRef.current) {
        waveSurferRef.current.destroy();
        waveSurferRef.current = null;
        // Notify parent that WaveSurfer is gone
        if (onWsReadyRef.current) onWsReadyRef.current(null);
      }
    };
  }, [activeTakeNumber, takes, compact, resolveTakeUrl]);

  // ---- A-B Loop Markers ----

  // Create/destroy A-B loop region when loopActive prop changes
  useEffect(() => {
    const ws = waveSurferRef.current;
    const regions = regionsPluginRef.current;
    if (!ws || !regions) return;

    const dur = ws.getDuration();
    if (!dur || dur <= 0) return;

    if (loopActive && !currentRegionRef.current) {
      // Create default A-B region at 25%-75%
      const start = dur * 0.25;
      const end = dur * 0.75;
      setLoopStart(start);
      setLoopEnd(end);

      const region = regions.addRegion({
        start,
        end,
        color: "rgba(197, 163, 78, 0.25)",
        drag: true,
        resize: true,
      });
      currentRegionRef.current = region;

      const handleUpdate = () => {
        setLoopStart(region.start);
        setLoopEnd(region.end);
        if (onLoopRegionChangeRef.current) {
          onLoopRegionChangeRef.current({
            start: region.start,
            end: region.end,
          });
        }
      };
      regionEventHandlerRef.current = handleUpdate;
      region.on("update-end", handleUpdate);

      if (onLoopRegionChangeRef.current) {
        onLoopRegionChangeRef.current({ start, end });
      }
    } else if (!loopActive && currentRegionRef.current) {
      // Destroy region
      if (regionEventHandlerRef.current) {
        try {
          currentRegionRef.current.un(
            "update-end",
            regionEventHandlerRef.current,
          );
        } catch {
          // Region may already be destroyed
        }
        regionEventHandlerRef.current = null;
      }
      currentRegionRef.current.remove();
      currentRegionRef.current = null;
      setLoopStart(null);
      setLoopEnd(null);
      if (onLoopRegionChangeRef.current) {
        onLoopRegionChangeRef.current(null);
      }
    }
  }, [loopActive, duration]);

  // A-B loop playback — timeupdate handler seeks to start when reaching end
  useEffect(() => {
    const ws = waveSurferRef.current;
    if (!ws || !loopActive) return;

    const handleTimeUpdate = (time) => {
      const region = currentRegionRef.current;
      if (!region) return;
      const LOOP_TOLERANCE = 0.1;
      if (time >= region.end - LOOP_TOLERANCE) {
        ws.seekTo(region.start / ws.getDuration());
      }
    };

    ws.on("timeupdate", handleTimeUpdate);
    return () => {
      ws.un("timeupdate", handleTimeUpdate);
    };
  }, [loopActive, duration]);

  // Loop nudge control functions
  const NUDGE_STEP = 0.1;

  const expandLoopLeft = useCallback((multiplier = 1) => {
    const region = currentRegionRef.current;
    const ws = waveSurferRef.current;
    if (!region || !ws) return;
    const step = NUDGE_STEP * multiplier;
    const newStart = Math.max(0, region.start - step);
    region.setOptions?.({ start: newStart });
    setLoopStart(newStart);
    if (onLoopRegionChangeRef.current) {
      onLoopRegionChangeRef.current({ start: newStart, end: region.end });
    }
  }, []);

  const expandLoopRight = useCallback((multiplier = 1) => {
    const region = currentRegionRef.current;
    const ws = waveSurferRef.current;
    if (!region || !ws) return;
    const step = NUDGE_STEP * multiplier;
    const maxEnd = ws.getDuration();
    const newEnd = Math.min(maxEnd, region.end + step);
    region.setOptions?.({ end: newEnd });
    setLoopEnd(newEnd);
    if (onLoopRegionChangeRef.current) {
      onLoopRegionChangeRef.current({ start: region.start, end: newEnd });
    }
  }, []);

  const contractLoopLeft = useCallback((multiplier = 1) => {
    const region = currentRegionRef.current;
    if (!region) return;
    const step = NUDGE_STEP * multiplier;
    const newStart = region.start + step;
    if (newStart < region.end) {
      region.setOptions?.({ start: newStart });
      setLoopStart(newStart);
      if (onLoopRegionChangeRef.current) {
        onLoopRegionChangeRef.current({ start: newStart, end: region.end });
      }
    }
  }, []);

  const contractLoopRight = useCallback((multiplier = 1) => {
    const region = currentRegionRef.current;
    if (!region) return;
    const step = NUDGE_STEP * multiplier;
    const newEnd = region.end - step;
    if (newEnd > region.start) {
      region.setOptions?.({ end: newEnd });
      setLoopEnd(newEnd);
      if (onLoopRegionChangeRef.current) {
        onLoopRegionChangeRef.current({ start: region.start, end: newEnd });
      }
    }
  }, []);

  // Toggle play/pause on active take
  const togglePlayPause = useCallback(() => {
    if (!waveSurferRef.current) return;
    if (playingTakeNumber === activeTakeNumber) {
      waveSurferRef.current.pause();
    } else {
      waveSurferRef.current.play();
    }
  }, [playingTakeNumber, activeTakeNumber]);

  // Volume control
  const handleVolumeChange = useCallback((val) => {
    if (!waveSurferRef.current) return;
    waveSurferRef.current.setVolume(val);
    setVolume(val);
    setIsMuted(val === 0);
  }, []);

  const toggleMute = useCallback(() => {
    if (!waveSurferRef.current) return;
    if (isMuted) {
      waveSurferRef.current.setVolume(volume || 0.5);
      setIsMuted(false);
    } else {
      waveSurferRef.current.setVolume(0);
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMenu]);

  // Start editing a take's label
  const startRename = (take) => {
    setEditingTakeNumber(take.takeNumber);
    setEditLabel(take.label || "");
  };

  // Save rename
  const confirmRename = async (take) => {
    const trimmed = editLabel.trim();
    if (trimmed && trimmed.length <= 100 && onRenameTake) {
      await onRenameTake(take, trimmed);
    }
    setEditingTakeNumber(null);
    setEditLabel("");
  };

  // Cancel rename
  const cancelRename = () => {
    setEditingTakeNumber(null);
    setEditLabel("");
  };

  // Handle tab click
  const handleTabClick = (takeNumber) => {
    if (onSelectTake) onSelectTake(takeNumber);
  };

  if (isLoadingTakes) {
    return (
      <div className="py-3 text-center text-forest-400 text-sm">
        Loading takes...
      </div>
    );
  }

  const activeTake = takes.find((t) => t.takeNumber === activeTakeNumber);

  // Format recording duration for regular recording
  const formatRecDuration = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Reusable mic/record button (pill-shaped, centered)
  const renderRecordButton = () => {
    const showPostRecording = !loopActive && recorder?.audioBlob && previewUrl;

    if (showPostRecording) return null; // Post-recording UI handles this

    return (
      <div className="flex justify-center">
        <button
          onClick={handleMicClick}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full transition-colors ${
            isAnyRecording
              ? "bg-ember-500 text-forest-900 border border-ember-400"
              : "bg-forest-700 hover:bg-forest-600 text-forest-200"
          }`}
          title={isAnyRecording ? "Stop recording" : "Start recording"}
        >
          <Mic
            className={`w-4 h-4 ${isAnyRecording ? "" : "text-ember-500"}`}
          />
          <span className="text-sm">{isAnyRecording ? "Stop" : "Record"}</span>
        </button>
      </div>
    );
  };

  // Reusable live waveform canvas
  const renderLiveWaveform = () => {
    if (!(isAnyRecording && getAnalyserData)) return null;
    return (
      <div className="mt-2">
        <canvas
          ref={liveCanvasRef}
          width={isMobile ? 200 : 400}
          height={isMobile ? 32 : 48}
          className="w-full rounded"
          style={{
            height: isMobile ? "32px" : "48px",
            background: "rgba(232, 93, 74, 0.08)",
          }}
        />
        {isRegularRecording && recorder && (
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-gold-500 font-mono">
              {formatRecDuration(recorder.recordingDuration)}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  recorder.isPaused
                    ? recorder.resumeRecording()
                    : recorder.pauseRecording()
                }
                className="w-7 h-7 flex items-center justify-center rounded-full bg-forest-700 hover:bg-forest-600 text-forest-200 transition-colors"
                title={recorder.isPaused ? "Resume" : "Pause"}
              >
                {recorder.isPaused ? (
                  <Play className="w-3.5 h-3.5" />
                ) : (
                  <Pause className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={() => recorder.stopRecording()}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-forest-700 hover:bg-forest-600 text-gold-500 transition-colors border border-gold-500/30"
                title="Stop recording"
              >
                <Square className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => recorder.cancelRecording()}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-forest-700 hover:bg-forest-600 text-forest-400 transition-colors"
                title="Cancel recording"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Reusable post-recording preview/save UI (regular recording only)
  const renderPostRecording = () => {
    if (loopActive || !recorder?.audioBlob || !previewUrl) return null;
    return (
      <div className="mt-3 p-3 rounded-lg border border-forest-600 bg-forest-900/80">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={togglePreview}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-forest-700 hover:bg-forest-600 text-gold-500"
          >
            {isPreviewPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </button>
          <span className="text-sm text-forest-200">
            {formatRecDuration(recorder.recordingDuration)}
          </span>
          <audio
            ref={previewAudioRef}
            src={previewUrl}
            onEnded={() => setIsPreviewPlaying(false)}
          />
        </div>
        <input
          type="text"
          value={labelInput}
          onChange={(e) => setLabelInput(e.target.value)}
          placeholder="Take name (optional)"
          className="w-full mb-2 px-2 py-1 text-sm bg-forest-900 border border-forest-600 rounded text-forest-200 placeholder-forest-500 focus:outline-none focus:border-gold-500"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleReRecord}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-forest-700 hover:bg-forest-600 text-forest-200 rounded transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Re-record
          </button>
          <button
            onClick={handleSaveRecording}
            disabled={isSaving}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-forest-700 hover:bg-forest-600 text-gold-500 rounded transition-colors border border-gold-500/30"
          >
            <Save className="w-3.5 h-3.5" />
            {isSaving ? "Saving..." : "Save Take"}
          </button>
        </div>
      </div>
    );
  };

  // Empty takes state — show record button only
  if (takes.length === 0) {
    return (
      <div
        className={`takes-player ${compact ? "p-2" : "p-4"} ${compact ? "" : "rounded-lg border border-forest-600 bg-forest-800"}`}
      >
        {renderRecordButton()}
        {renderLiveWaveform()}
        {renderPostRecording()}
      </div>
    );
  }

  return (
    <div
      className={`takes-player ${compact ? "p-2" : "p-4"} ${compact ? "" : "rounded-lg border border-forest-600 bg-forest-800"}`}
    >
      {/* Take tabs */}
      <div className="flex items-center gap-1 mb-2 overflow-x-auto scrollbar-hide">
        {takes.map((take) => (
          <button
            key={take.takeNumber}
            onClick={() => handleTabClick(take.takeNumber)}
            className={`flex-shrink-0 px-2.5 py-1 text-xs rounded-t transition-colors ${
              activeTakeNumber === take.takeNumber
                ? "bg-forest-700 text-gold-500 border-b-2 border-gold-500"
                : "bg-forest-900 text-forest-300 hover:bg-forest-700 hover:text-forest-200"
            }`}
          >
            {take.label || `Take ${take.takeNumber}`}
          </button>
        ))}
      </div>

      {/* Active take waveform + controls — matches AudioPlayer structure */}
      {activeTake && (
        <div className="space-y-3">
          {/* Waveform + time display */}
          <div className="space-y-1">
            {/* Outer canvas — represents beat timeline when beat is loaded */}
            <div
              style={{
                height: compact ? "16px" : "24px",
                width: "100%",
                backgroundColor: "#2e3e3a",
                border: "1px solid #4a6057",
                borderRadius: "4px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {/* Inner waveform host — always proportional to beat when beat is loaded */}
              <div
                ref={containerRef}
                className="waveform-container"
                style={{
                  height: "100%",
                  width:
                    beatDuration > 0
                      ? `${(duration / beatDuration) * 100}%`
                      : "100%",
                  marginLeft:
                    beatDuration > 0
                      ? `${((showShiftView ? tempShiftOffset : activeTake?.shiftOffsetSec || 0) / beatDuration) * 100}%`
                      : "0",
                  transition: showShiftView
                    ? "margin-left 0.1s ease-out"
                    : "none",
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-forest-300">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration || activeTake.duration)}</span>
            </div>
          </div>

          {/* Controls */}
          {isMobile ? (
            /* Mobile: controls row + centered record button below */
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {/* Play/Pause — same size as volume button */}
                <button
                  onClick={togglePlayPause}
                  className="flex-shrink-0 p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                  title={
                    playingTakeNumber === activeTakeNumber
                      ? "Pause"
                      : "Play take"
                  }
                >
                  {playingTakeNumber === activeTakeNumber ? (
                    <Pause className="w-3.5 h-3.5" />
                  ) : (
                    <Play className="w-3.5 h-3.5 ml-0.5" />
                  )}
                </button>

                {/* Loop button — inline, same size as volume */}
                {onToggleLoop && (
                  <button
                    onClick={onToggleLoop}
                    className={`flex-shrink-0 p-1 rounded transition-colors ${
                      loopActive
                        ? "bg-forest-600 text-gold-500 border border-gold-500/30"
                        : "bg-forest-700 hover:bg-forest-600 text-forest-300"
                    }`}
                    title={loopActive ? "Loop on" : "Loop off"}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}

                {/* Volume button */}
                <button
                  onClick={toggleMute}
                  className="flex-shrink-0 p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                  title="Volume"
                >
                  {isMuted ? (
                    <VolumeX className="w-3.5 h-3.5" />
                  ) : (
                    <Volume2 className="w-3.5 h-3.5" />
                  )}
                </button>

                {/* Volume slider OR Rename input — fills remaining space */}
                {editingTakeNumber === activeTake.takeNumber ? (
                  <div className="flex-1 min-w-0 flex items-center gap-1">
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") confirmRename(activeTake);
                        if (e.key === "Escape") cancelRename();
                      }}
                      maxLength={100}
                      className="flex-1 px-1.5 py-0.5 text-xs bg-forest-900 border border-forest-600 rounded text-forest-200 focus:outline-none focus:border-gold-500"
                      autoFocus
                    />
                    <button
                      onClick={() => confirmRename(activeTake)}
                      className="p-0.5 text-green-500 hover:text-green-400"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={cancelRename}
                      className="p-0.5 text-forest-400 hover:text-forest-300"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={(e) =>
                      handleVolumeChange(parseFloat(e.target.value))
                    }
                    className="h-2 rounded-lg appearance-none cursor-pointer bg-forest-700"
                    style={{
                      flex: "1 1 0%",
                      minWidth: 0,
                      accentColor: "#c5a34e",
                      background: `linear-gradient(to right, #c5a34e 0%, #c5a34e ${(isMuted ? 0 : volume) * 100}%, #2e3e3a ${(isMuted ? 0 : volume) * 100}%, #2e3e3a 100%)`,
                    }}
                  />
                )}

                {/* Menu button */}
                {(onRenameTake || onDeleteTake) &&
                  editingTakeNumber !== activeTake.takeNumber && (
                    <button
                      ref={menuButtonRef}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowMenu(!showMenu);
                      }}
                      className="flex-shrink-0 p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  )}
              </div>

              {/* Mic/Record button — centered row beneath controls */}
              {renderRecordButton()}

              {/* Live waveform canvas — shown when recording */}
              {renderLiveWaveform()}

              {/* Post-recording preview/save */}
              {renderPostRecording()}
            </div>
          ) : (
            /* Desktop: left/right flex with absolutely centered Record button */
            <>
              <div className="relative flex items-center justify-between">
                {/* Left: Play, Loop, nudge controls */}
                <div className="flex items-center gap-2">
                  {/* Play/Pause */}
                  <button
                    onClick={togglePlayPause}
                    className="flex items-center justify-center w-10 h-10 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                    title={
                      playingTakeNumber === activeTakeNumber
                        ? "Pause"
                        : "Play take"
                    }
                  >
                    {playingTakeNumber === activeTakeNumber ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5 ml-0.5" />
                    )}
                  </button>

                  {/* Loop button — inline, same position as AudioPlayer */}
                  {onToggleLoop && (
                    <button
                      onClick={onToggleLoop}
                      className={`p-1 rounded transition-colors ${
                        loopActive
                          ? "bg-forest-600 text-gold-500 border border-gold-500/30"
                          : "bg-forest-700 hover:bg-forest-600 text-forest-300"
                      }`}
                      title={loopActive ? "Loop on" : "Loop off"}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}

                  {/* Loop nudge controls — inline after loop button (desktop only) */}
                  {loopActive && (
                    <LoopNudgeControls
                      inline
                      onExpandLeft={expandLoopLeft}
                      onExpandRight={expandLoopRight}
                      onContractLeft={contractLoopLeft}
                      onContractRight={contractLoopRight}
                      disabled={
                        loopRecordState !== "idle" &&
                        loopRecordState !== "preview"
                      }
                    />
                  )}
                </div>

                {/* Center: Mic/Record button — absolutely positioned for true centering */}
                {editingTakeNumber === activeTake.takeNumber ? (
                  <div
                    className="flex items-center gap-1"
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  >
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") confirmRename(activeTake);
                        if (e.key === "Escape") cancelRename();
                      }}
                      maxLength={100}
                      className="w-40 px-1.5 py-0.5 text-xs bg-forest-900 border border-forest-600 rounded text-forest-200 focus:outline-none focus:border-gold-500"
                      autoFocus
                    />
                    <button
                      onClick={() => confirmRename(activeTake)}
                      className="p-0.5 text-green-500 hover:text-green-400"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={cancelRename}
                      className="p-0.5 text-forest-400 hover:text-forest-300"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (recorder || onStartLoopRecord) &&
                  !(recorder?.audioBlob && previewUrl && !loopActive) ? (
                  <button
                    onClick={handleMicClick}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full transition-colors ${
                      isAnyRecording
                        ? "bg-ember-500 text-forest-900 border border-ember-400"
                        : "bg-forest-700 hover:bg-forest-600 text-forest-200"
                    }`}
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                    title={
                      isAnyRecording ? "Stop recording" : "Start recording"
                    }
                  >
                    <Mic
                      className={`w-4 h-4 ${isAnyRecording ? "" : "text-ember-500"}`}
                    />
                    <span className="text-sm">
                      {isAnyRecording ? "Stop" : "Record"}
                    </span>
                  </button>
                ) : null}

                {/* Right: Volume, slider, menu */}
                <div className="flex items-center gap-2">
                  {/* Volume */}
                  <button
                    onClick={toggleMute}
                    className="p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                    title="Volume"
                  >
                    {isMuted ? (
                      <VolumeX className="w-4 h-4" />
                    ) : (
                      <Volume2 className="w-4 h-4" />
                    )}
                  </button>

                  {/* Volume slider */}
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={(e) =>
                      handleVolumeChange(parseFloat(e.target.value))
                    }
                    className="w-32 h-2 rounded-lg appearance-none cursor-pointer bg-forest-700"
                    style={{
                      accentColor: "#c5a34e",
                      background: `linear-gradient(to right, #c5a34e 0%, #c5a34e ${(isMuted ? 0 : volume) * 100}%, #2e3e3a ${(isMuted ? 0 : volume) * 100}%, #2e3e3a 100%)`,
                    }}
                  />

                  {/* Menu button */}
                  {(onRenameTake || onDeleteTake) &&
                    editingTakeNumber !== activeTake.takeNumber && (
                      <div className="relative ml-2">
                        <button
                          ref={menuButtonRef}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowMenu(!showMenu);
                          }}
                          className="p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                </div>
              </div>

              {/* Live waveform canvas — shown below controls when recording */}
              {renderLiveWaveform()}

              {/* Post-recording preview/save (regular recording) */}
              {renderPostRecording()}
            </>
          )}
        </div>
      )}

      {/* Portal menu for rename/delete — matches AudioPlayer menu */}
      {showMenu &&
        activeTake &&
        (onRenameTake || onDeleteTake) &&
        (() => {
          const menuItems = [];
          // Shift — in menu on both desktop and mobile (disabled when loop active)
          if (onShiftOffsetChange) {
            menuItems.push({
              key: "shift",
              label: "Shift",
              icon: MoveHorizontal,
              onClick: loopActive
                ? null
                : () => {
                    setTempShiftOffset(activeTake?.shiftOffsetSec || 0);
                    setShowShiftView(true);
                    setShowMenu(false);
                  },
              danger: false,
              disabled: loopActive,
            });
          }
          if (onRenameTake) {
            menuItems.push({
              key: "rename",
              label: "Rename",
              icon: Pencil,
              onClick: () => {
                startRename(activeTake);
                setShowMenu(false);
              },
              danger: false,
            });
          }
          menuItems.push({
            key: "download",
            label: "Download",
            icon: Download,
            onClick: () => {
              handleDownloadTake(activeTake);
              setShowMenu(false);
            },
            danger: false,
          });
          if (onDeleteTake) {
            menuItems.push({
              key: "delete",
              label: "Delete",
              icon: Trash2,
              onClick: () => {
                onDeleteTake(activeTake);
                setShowMenu(false);
              },
              danger: true,
            });
          }

          return createPortal(
            isMobile ? (
              /* Mobile: backdrop + slide-up panel */
              <>
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 99998,
                    background: "rgba(0,0,0,0.3)",
                  }}
                  onClick={() => setShowMenu(false)}
                />
                <div
                  ref={dropdownRef}
                  style={{
                    position: "fixed",
                    bottom:
                      "max(84px, calc(68px + env(safe-area-inset-bottom, 0px)))",
                    left: 0,
                    right: 0,
                    zIndex: 99999,
                    animation: "takeMenuSlideUp 0.2s ease-out",
                  }}
                >
                  <div
                    className="bg-forest-800 border-t border-forest-700"
                    style={{ display: "block", padding: "6px 8px" }}
                  >
                    {menuItems.map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.key}
                          disabled={item.disabled}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!item.disabled && item.onClick) item.onClick();
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            width: "100%",
                            fontSize: "14px",
                            padding: "10px 12px",
                            borderRadius: "6px",
                            marginBottom:
                              idx < menuItems.length - 1 ? "4px" : "0",
                            opacity: item.disabled ? 0.4 : 1,
                            cursor: item.disabled ? "not-allowed" : "pointer",
                          }}
                          className={`transition-colors ${
                            item.danger
                              ? "bg-ember-900/30 hover:bg-ember-900/50 text-ember-300"
                              : "bg-forest-700 hover:bg-forest-600 text-forest-200"
                          }`}
                        >
                          <Icon
                            style={{
                              width: "20px",
                              height: "20px",
                              flexShrink: 0,
                            }}
                            className={
                              item.danger ? "text-ember-400" : "text-gold-500"
                            }
                          />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <style>{`
                  @keyframes takeMenuSlideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                  }
                `}</style>
              </>
            ) : (
              /* Desktop: fixed dropdown below button */
              <div
                ref={dropdownRef}
                style={{
                  position: "fixed",
                  zIndex: 99999,
                  top: menuButtonRef.current
                    ? `${menuButtonRef.current.getBoundingClientRect().bottom + 4}px`
                    : "auto",
                  right: menuButtonRef.current
                    ? `${window.innerWidth - menuButtonRef.current.getBoundingClientRect().right}px`
                    : "auto",
                }}
              >
                <div
                  className="bg-forest-800 border border-forest-700 rounded-lg shadow-xl"
                  style={{ display: "block", padding: "4px" }}
                >
                  {menuItems.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        disabled={item.disabled}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!item.disabled && item.onClick) item.onClick();
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          width: "100%",
                          textAlign: "left",
                          fontSize: "12px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          marginBottom:
                            idx < menuItems.length - 1 ? "2px" : "0",
                          opacity: item.disabled ? 0.4 : 1,
                          cursor: item.disabled ? "not-allowed" : "pointer",
                        }}
                        className={`transition-colors ${
                          item.danger
                            ? "bg-ember-900/30 hover:bg-ember-900/50 text-ember-300"
                            : "bg-forest-700 hover:bg-forest-600 text-forest-200"
                        }`}
                      >
                        <Icon
                          style={{
                            width: "16px",
                            height: "16px",
                            flexShrink: 0,
                          }}
                          className={
                            item.danger ? "text-ember-400" : "text-gold-500"
                          }
                        />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ),
            document.body,
          );
        })()}

      {/* Shift inline view */}
      {showShiftView && activeTake && (
        <div className="mt-3 p-3 rounded-lg border border-forest-600 bg-forest-900/80">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-forest-200">
              Shift Take
            </span>
            <span className="text-xs text-forest-300 font-mono">
              {tempShiftOffset.toFixed(2)}s
            </span>
          </div>
          {/* Slider — range is [0, beatDuration] representing position on the beat timeline */}
          <input
            type="range"
            min="0"
            max={beatDuration || 5}
            step="0.01"
            value={tempShiftOffset}
            onChange={(e) => setTempShiftOffset(parseFloat(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer mb-2"
            style={{
              accentColor: "#c5a34e",
              background: `linear-gradient(to right, #c5a34e 0%, #c5a34e ${(tempShiftOffset / (beatDuration || 5)) * 100}%, #2e3e3a ${(tempShiftOffset / (beatDuration || 5)) * 100}%, #2e3e3a 100%)`,
            }}
          />
          {/* Nudge buttons — clamp to [0, beatDuration] */}
          <div className="flex items-center justify-center gap-1 mb-2">
            {[-0.05, -0.01, 0.01, 0.05].map((nudge) => (
              <button
                key={nudge}
                onClick={() =>
                  setTempShiftOffset((v) =>
                    Math.max(
                      0,
                      Math.min(beatDuration || 5, +(v + nudge).toFixed(2)),
                    ),
                  )
                }
                className="px-2 py-1 text-xs rounded bg-forest-700 hover:bg-forest-600 text-forest-200 transition-colors"
              >
                {nudge > 0 ? "+" : ""}
                {nudge}s
              </button>
            ))}
            <button
              onClick={() => setTempShiftOffset(0)}
              className="px-2 py-1 text-xs rounded bg-forest-700 hover:bg-forest-600 text-forest-300 transition-colors"
            >
              Reset
            </button>
          </div>
          {/* Apply / Cancel */}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setShowShiftView(false)}
              className="px-3 py-1 text-xs rounded bg-forest-700 hover:bg-forest-600 text-forest-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (onShiftOffsetChange) {
                  onShiftOffsetChange(activeTake, tempShiftOffset);
                }
                setShowShiftView(false);
              }}
              className="px-3 py-1 text-xs rounded bg-gold-500 hover:bg-gold-400 text-forest-900 font-medium transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Loop-record status: pre-roll / recording / splicing */}
      {(loopRecordState === "pre_roll" ||
        loopRecordState === "recording" ||
        loopRecordState === "splicing") && (
        <div className="mt-3 p-3 rounded-lg border border-ember-500/30 bg-ember-900/20">
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-medium ${
                loopRecordState === "recording"
                  ? "text-ember-400 animate-pulse"
                  : "text-forest-200"
              }`}
            >
              {loopRecordState === "pre_roll" && "Pre-roll playing..."}
              {loopRecordState === "recording" && "Recording..."}
              {loopRecordState === "splicing" && "Splicing audio..."}
            </span>
            <div className="flex-1" />
            {loopRecordState === "recording" && onStopLoopRecord && (
              <button
                onClick={onStopLoopRecord}
                className="px-3 py-1 text-xs rounded bg-gold-500 hover:bg-gold-400 text-forest-900 font-medium transition-colors"
              >
                Stop
              </button>
            )}
            {onCancelLoopRecord && loopRecordState !== "splicing" && (
              <button
                onClick={onCancelLoopRecord}
                className="px-3 py-1 text-xs rounded bg-forest-700 hover:bg-forest-600 text-forest-200 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Loop-record error */}
      {loopRecordError && loopRecordState === "idle" && (
        <div className="mt-3 p-3 rounded-lg border border-ember-500/30 bg-ember-900/20">
          <p className="text-xs text-ember-300">{loopRecordError}</p>
        </div>
      )}

      {/* Loop-record preview — keep or discard the splice */}
      {loopRecordState === "preview" && loopRecordSplicedBlob && (
        <div className="mt-3 p-3 rounded-lg border border-gold-500/30 bg-forest-900/80">
          <p className="text-xs text-forest-200 mb-2">
            Recording spliced. Keep this change?
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1" />
            {onRetrySplice && (
              <button
                onClick={onRetrySplice}
                className="px-3 py-1 text-xs rounded bg-forest-700 hover:bg-forest-600 text-forest-200 transition-colors"
              >
                Discard
              </button>
            )}
            {onAcceptSplice && (
              <button
                onClick={onAcceptSplice}
                className="px-3 py-1 text-xs rounded bg-gold-500 hover:bg-gold-400 text-forest-900 font-medium transition-colors"
              >
                Keep
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TakesList;
